/**
 * Client Portal V2 - Documents Page
 * ==================================
 * Central document repository.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import ClientLayout from "@/components/client/ClientLayout";
import {
  ClientKpiCard, ClientEmptyState, ClientLoadingState, 
  ClientErrorState, ClientDocumentItem
} from "@/components/client/ClientComponents";
import { 
  FolderOpen, Search, Download, FileText, 
  FileCheck, Image, File
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ClientDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 50, total_pages: 1 });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const docType = searchParams.get("type") || "";
  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const params = new URLSearchParams();
      if (docType) params.set("doc_type", docType);
      if (search) params.set("search", search);
      params.set("page", page.toString());
      params.set("page_size", "50");

      const res = await apiClient.get(`/api/client/documents?${params}`);
      
      if (res.data?.success) {
        setDocuments(res.data.data || []);
        setMeta(res.data.meta || {});
      }
    } catch (err) {
      console.error("Failed to load documents:", err);
      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }
      setError("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [docType, search, page]);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    fetchDocuments();
  }, [fetchDocuments, navigate]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateFilter("search", formData.get("search"));
  };

  // Count by category
  const certificates = documents.filter(d => d.type?.toLowerCase().includes("certificate")).length;
  const photos = documents.filter(d => d.type?.toLowerCase().includes("photo")).length;
  const jobSheets = documents.filter(d => d.type?.toLowerCase().includes("job") || d.type?.toLowerCase().includes("sheet")).length;
  const other = documents.length - certificates - photos - jobSheets;

  return (
    <ClientLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
        <p className="text-slate-500 mt-1">Certificates, job sheets, photos, and reports</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <ClientKpiCard
          title="Total Documents"
          value={meta.total}
          icon={FolderOpen}
          color="teal"
        />
        <ClientKpiCard
          title="Certificates"
          value={certificates}
          icon={FileCheck}
          color="green"
          onClick={() => updateFilter("type", "certificate")}
        />
        <ClientKpiCard
          title="Photos"
          value={photos}
          icon={Image}
          color="blue"
          onClick={() => updateFilter("type", "photo")}
        />
        <ClientKpiCard
          title="Job Sheets"
          value={jobSheets}
          icon={FileText}
          color="purple"
          onClick={() => updateFilter("type", "sheet")}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="search"
                placeholder="Search by name, type..."
                defaultValue={search}
                className="pl-10"
              />
            </div>
          </form>

          <Select value={docType || "all"} onValueChange={(v) => updateFilter("type", v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="certificate">Certificates</SelectItem>
              <SelectItem value="photo">Photos</SelectItem>
              <SelectItem value="sheet">Job Sheets</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="quote">Quotes</SelectItem>
            </SelectContent>
          </Select>

          {(docType || search) && (
            <Button
              variant="ghost"
              onClick={() => setSearchParams({})}
              className="shrink-0"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Documents Grid */}
      {loading ? (
        <ClientLoadingState message="Loading documents..." />
      ) : error ? (
        <ClientErrorState message={error} onRetry={fetchDocuments} />
      ) : documents.length === 0 ? (
        <ClientEmptyState
          icon={FolderOpen}
          title="No documents found"
          description={docType || search ? "Try adjusting your filters" : "No documents have been uploaded yet"}
        />
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <ClientDocumentItem key={doc.id} doc={doc} />
          ))}

          {/* Pagination */}
          {meta.total_pages > 1 && (
            <div className="pt-4 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {meta.page} of {meta.total_pages} ({meta.total} documents)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => updateFilter("page", String(meta.page - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => updateFilter("page", String(meta.page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </ClientLayout>
  );
}
